import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/shell/PasswordField';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, ShieldCheck, CheckCircle, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';
import { BRAND_NAME } from '../brand';

export default function SetupPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Check if setup is required on mount
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/setup/status`);
        if (!response.data.setup_required) {
          // Super Admin already exists, redirect to login
          toast.info('Setup already completed. Please login.');
          navigate('/login');
        }
      } catch (error) {
        // If API fails, allow setup attempt
        console.error('Failed to check setup status:', error);
      } finally {
        setChecking(false);
      }
    };
    checkSetupStatus();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/setup/super-admin`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name
      });
      setSuccess(true);
      toast.success('Super Admin account created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Setup failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking setup status
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <SpinnerGap className="w-10 h-10 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Checking setup status...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" weight="fill" />
            </div>
            <CardTitle className="font-heading text-2xl text-green-700">Setup Complete!</CardTitle>
            <CardDescription>Your Super Admin account has been created</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium">{formData.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Role:</span>
                <span className="font-medium">Super Admin</span>
              </div>
            </div>

            <Button 
              className="w-full" 
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-primary-foreground" weight="duotone" />
            </div>
            <span className="font-heading font-bold text-xl">{BRAND_NAME}</span>
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-6 h-6 text-primary" weight="duotone" />
                <span className="text-sm font-medium text-primary">First Time Setup</span>
              </div>
              <CardTitle className="font-heading text-2xl">Create Super Admin</CardTitle>
              <CardDescription>Set up your administrator account to get started</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="field">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="John"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="field">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Doe"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div className="field">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="admin@yourcompany.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                  />
                </div>

                <PasswordField
  id="password"
  label="Password"
  value={formData.password}
  onChange={(v) => handleChange({ target: { name: 'password', value: v } })}
  placeholder="Min 6 characters"
  required
  minLength={6}
  testid="password-input"
  generate
/>

                <Button type="submit" className="w-full" disabled={loading} data-testid="setup-submit">
                  {loading ? 'Creating Account...' : 'Create Super Admin Account'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Info */}
      <div className="hidden lg:block relative bg-gradient-to-br from-primary to-primary/80">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="absolute inset-0 flex flex-col justify-center p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Welcome to {BRAND_NAME}
          </h2>
          <p className="text-lg opacity-90 mb-8">
            Set up your Super Admin account to start managing your learning management system.
          </p>
          <ul className="space-y-4 text-lg opacity-90">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Manage tenants and organizations</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Configure platform-wide settings</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5 flex-shrink-0" weight="fill" />
              <span>Monitor system health and usage</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
